import React, { useRef, useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import clsx from 'clsx';
import { reaction } from 'mobx';
import './SequenceClip.styles.scss';
import {InteractiveClip} from "../../librettoEditor/modules/timeline/utils/InteractiveClip";
import {SequenceTimelineStore} from "../store/SequenceTimelineStore";
import {convertTimeToUnits} from "../../librettoEditor/utils/dom/convertTimeToUnits";
import {SequenceStateStore} from "../store/SequenceStateStore";
import {updateAdjacency} from "../../librettoEditor/modules/timeline/utils/updateLayerClipsAdjacency";
import {ResizeDirectionEnum} from "../../librettoEditor/modules/timeline/utils/InteractiveClip";
import {SequenceApplicationStore} from "../store/SequenceApplicationStore";
import {convertUnitsToTime} from "../../librettoEditor/utils/dom/convertUnitsToTime";
import {SequenceCompositionService} from "../services/SequenceCompositionService";
import {generateClipIdentifierClass} from "../../librettoEditor/modules/timeline/utils/generateClipIdentifierClass";
import {CLIP_CLASSNAME, TRACK_CLASSNAME, TIMELINE_TRACKS_CONTAINER_ID} from "../config/constants";
import {TrashIcon} from "../../librettoEditor/assets/icons/TrashIcon";
import {ContextMenuStore} from "../../librettoEditor/store/ContextMenuStore";
import {getPlatform} from "../../librettoEditor/utils/shortcuts";
import CopyIcon from "@mui/icons-material/ContentCopy";
import ContentCutIcon from "@mui/icons-material/ContentCut";

const SNAP_THRESHOLD = 10;

interface ClipProps {
    clipId: string;
    layerId: string;
    filename?: string;
    thumbnail?: string;
    isResizeEnabled?: boolean;
    backgroundColor?: string;
    children?: React.ReactNode;
    /** Optional content to render in a dedicated words strip at the top of the clip */
    wordsContent?: React.ReactNode;
}

export const SequenceClip: React.FC<ClipProps> = observer(({ clipId, layerId, filename = "Unknown", thumbnail, isResizeEnabled, backgroundColor = "43,98,253", children, wordsContent }) => {
    const [startTime, setStartTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [trimmedDuration, setTrimmedDuration] = useState(0);
    const [width, setWidth] = useState(200);

    const xRef = useRef(0);
    const yRef = useRef(0);

    const startTimeRef = useRef(0);
    const durationRef = useRef(0);


    const [x, setX] = useState(0);
    const [y, setY] = useState(0);

    const [isFocused, setIsFocused] = useState(false);
    const [isSelected, setIsSelected] = useState(false);
    const [hasSuccessor, setHasSuccessor] = useState(false);
    const [hasPredecessor, setHasPredecessor] = useState(false);

    const elementRef = useRef<HTMLDivElement>(null);
    const interactionRef = useRef<InteractiveClip | null>(null);
    const initialPositionRef = useRef({ top: 0, height: 0, left: 0, width: 0, x: 0, clientXOffset: 0, clientYOffset: 0 });
    const layerSnapPositionsRef = useRef<{ top: number; height: number; layerId: string }[]>([]);
    const clipSnapPositionsRef = useRef<{ x: number; left: number; width: number; clipId: string }[]>([]);
    const focusEventRef = useRef<MouseEvent | null>(null);

    const clipStyle = backgroundColor ? {
        '--clip-background-color': backgroundColor
    } as React.CSSProperties : {};

    useEffect(() => {
        const disposeReaction = reaction(
            () => SequenceTimelineStore.zoom,
            () => {
                const clip = SequenceStateStore.clips?.[clipId];
                if (clip == null) return;
                // x is based on timeline start only; clamp to timeline >= 0
                xRef.current = Math.max(0, convertTimeToUnits(clip.startTime, true));
                setX(xRef.current);
                setWidth(convertTimeToUnits(clip.trimmedDuration, true));
            }
        );

        return () => {
            disposeReaction();
        };
    }, [clipId]);

    useEffect(() => {
        const disposer = reaction(
            () => ({
                clip: SequenceStateStore.clips?.[clipId],
                selectedClipId: SequenceApplicationStore.selectedClipId,
                selectedClipIds: SequenceApplicationStore.selectedClipIds,
            }),
            ({ clip, selectedClipId, selectedClipIds }) => {
                if (!clip) return;
                startTimeRef.current = clip.startTime;
                setStartTime(clip.startTime);
                durationRef.current = clip.duration;
                setDuration(clip.duration);
                setTrimmedDuration(clip.trimmedDuration);
                setHasPredecessor(clip.hasPredecessor ?? false);
                setHasSuccessor(clip.hasSuccessor ?? false);
                setIsFocused(selectedClipId === clip.id);
                const isClipSelected = Array.isArray(selectedClipIds) && selectedClipIds.includes(clip.id);
                setIsSelected(isClipSelected || selectedClipId === clip.id);
            },
            { fireImmediately: true }
        );

        return () => {
            disposer();
        };
    }, [clipId]);

    useEffect(() => {
        xRef.current = Math.max(0, convertTimeToUnits(startTime, true));
        setX(xRef.current);
        setWidth(convertTimeToUnits(trimmedDuration, true));
        updateAdjacency(layerId);
    }, [startTime, trimmedDuration, layerId]);

    useEffect(() => {
        if (elementRef.current == null || elementRef.current.parentElement?.parentElement == null) return;

        interactionRef.current = new InteractiveClip(elementRef.current, elementRef.current.parentElement.parentElement, {
            onDragStart: dragStart,
            onResizeStart: resizeStart,
            onDrag: drag,
            onResize: resize,
            onResizeEnd: resizeEnd,
            onDragEnd: dragEnd,
        });

        handleUpdateStyles();

        return () => {
            interactionRef.current?.destroy();
        };
    }, []);

    useEffect(() => {
        handleUpdateStyles();
    }, [hasPredecessor, hasSuccessor, width, xRef.current, yRef.current]);

    const handleFocusChange = () => {
        if (interactionRef.current == null) {
            // give the interaction enough time to mount
            setTimeout(() => {
                handleFocusChange();
            });
            return;
        }

        if (isFocused && !SequenceApplicationStore.rangeToolEnabled) {
            // @ts-ignore
            interactionRef.current.mount(focusEventRef.current, { isResizeEnabled });
        } else {
            interactionRef.current.destroy();
            focusEventRef.current = null;
        }

        if (elementRef.current != null) {
            elementRef.current.style.zIndex = isFocused ? "2" : "1";
        }
    };

    useEffect(() => {
        handleFocusChange();
    }, [isFocused, SequenceApplicationStore.rangeToolEnabled]);

    const handleUpdateStyles = () => {
        if (elementRef.current == null) return;

        if (hasPredecessor && hasSuccessor) {
            elementRef.current.style.width = `calc(${width}px - 2px)`;
            elementRef.current.style.transform = `translate(calc(${xRef.current}px + 1px), ${yRef.current}px)`;
        } else if (hasSuccessor) {
            elementRef.current.style.width = `calc(${width}px - 1px)`;
            elementRef.current.style.transform = `translate(${xRef.current}px, ${yRef.current}px)`;
        } else if (hasPredecessor) {
            elementRef.current.style.width = `calc(${width}px - 1px)`;
            elementRef.current.style.transform = `translate(calc(${xRef.current}px + 1px), ${yRef.current}px)`;
        } else {
            elementRef.current.style.width = `${width}px`;
            elementRef.current.style.transform = `translate(${xRef.current}px, ${yRef.current}px)`;
        }

        interactionRef.current?.moveable?.updateRect();
    };

    const drag = (clientX: number, _clientY: number) => {
        // Only allow horizontal dragging - no layer changes
        const deltaX = clientX - initialPositionRef.current.left;
        let newX = initialPositionRef.current.x + deltaX - initialPositionRef.current.clientXOffset;

        const currentClipLeft = newX;
        const currentClipRight = currentClipLeft + width;

        // Snap to other clips horizontally
        for (let i = 0; i < clipSnapPositionsRef.current.length; i++) {
            const { x: clipX, width: clipWidth } = clipSnapPositionsRef.current[i];
            const clipRight = clipX + clipWidth;

            const diffLeft = Math.abs(currentClipLeft - clipX);
            const diffRight = Math.abs(currentClipRight - clipRight);
            const diffRightLeft = Math.abs(currentClipRight - clipX);
            const diffLeftRight = Math.abs(currentClipLeft - clipRight);

            if (diffLeft <= SNAP_THRESHOLD) {
                newX = clipX;
                break;
            } else if (diffRight <= SNAP_THRESHOLD) {
                newX = clipRight - width;
                break;
            } else if (diffRightLeft <= SNAP_THRESHOLD) {
                newX = clipX - width;
                break;
            } else if (diffLeftRight <= SNAP_THRESHOLD) {
                newX = clipRight;
                break;
            }
        }

        // Keep Y fixed (no layer changes)
        // yRef.current stays at 0
        xRef.current = newX;
        setX(newX);
    };

    const resize = (clientX: number, direction: ResizeDirectionEnum) => {
        if (SequenceStateStore.clips?.[clipId]?.type === "subtitles") {
            return;
        }

        const MARGIN_PADDING = 6;

        let deltaX = 0;
        let newWidth = initialPositionRef.current.width;

        if (direction === ResizeDirectionEnum.LEFT) {
            deltaX = clientX - initialPositionRef.current.left - MARGIN_PADDING;
            newWidth = initialPositionRef.current.left + initialPositionRef.current.width - clientX + MARGIN_PADDING;
        } else if (direction === ResizeDirectionEnum.RIGHT) {
            newWidth = clientX - initialPositionRef.current.left + MARGIN_PADDING;
            deltaX = 0;
        }

        let newX = initialPositionRef.current.x + deltaX;
        const currentClipLeft = newX;
        const currentClipRight = currentClipLeft + newWidth;

        for (let i = 0; i < clipSnapPositionsRef.current.length; i++) {
            const { x: clipX, width: clipWidth } = clipSnapPositionsRef.current[i];
            const clipRight = clipX + clipWidth;

            const diffLeft = Math.abs(currentClipLeft - clipX);
            const diffRight = Math.abs(currentClipRight - clipRight);
            const diffRightLeft = Math.abs(currentClipRight - clipX);
            const diffLeftRight = Math.abs(currentClipLeft - clipRight);

            if (direction === ResizeDirectionEnum.LEFT) {
                if (diffLeft <= SNAP_THRESHOLD) {
                    newX = clipX;
                    newWidth = initialPositionRef.current.width + (initialPositionRef.current.x - newX);
                    break;
                } else if (diffLeftRight <= SNAP_THRESHOLD) {
                    newX = clipRight;
                    newWidth = initialPositionRef.current.width + (initialPositionRef.current.x - newX);
                    break;
                }
            } else if (direction === ResizeDirectionEnum.RIGHT) {
                if (diffRight <= SNAP_THRESHOLD) {
                    newWidth = clipRight - currentClipLeft;
                    break;
                } else if (diffRightLeft <= SNAP_THRESHOLD) {
                    newWidth = clipX - currentClipLeft;
                    break;
                }
            }
        }

        // Prevent visual positioning before timeline start
        if (direction === ResizeDirectionEnum.LEFT) {
            // Recompute width if clamping left edge to 0
            if (newX < 0) {
                newWidth = currentClipRight - 0;
                newX = 0;
            }
        }
        xRef.current = Math.max(0, newX);
        setX(newX);
        setWidth(newWidth);
        // Live-preview trim so waveform reflects current in/out while dragging
        if (direction === ResizeDirectionEnum.LEFT) {
            const deltaUnits = newX - initialPositionRef.current.x; // >0 when moving right
            const deltaSec = convertUnitsToTime(deltaUnits, true);        // can be negative (extend)
            // SequenceTimelineStore.setLeftTrimPreview(clipId, deltaSec);
        } else if (direction === ResizeDirectionEnum.RIGHT) {
            const deltaUnits = newWidth - initialPositionRef.current.width; // >0 extend, <0 shrink
            const deltaSec = convertUnitsToTime(deltaUnits, true);
        //     TimelineStore.setRightTrimPreview(clipId, deltaSec);
        }
    };

    const resizeStart = () => {
        if (SequenceStateStore.clips?.[clipId]?.type === "subtitles") {
            return;
        }

        if (!elementRef.current) {
            return;
        }

        const { top, height, left, width } = elementRef.current.getBoundingClientRect();
        initialPositionRef.current = { top, height, left, width, x: xRef.current, clientXOffset: 0, clientYOffset: 0 };

        SequenceTimelineStore.setDraggingClip(clipId, true);
        setClipSnapPositions();
    };

    const resizeEnd = (direction: ResizeDirectionEnum, distance: number) => {
        const clip = SequenceCompositionService.getClipById(clipId);
        if (!clip) return;

        if (SequenceStateStore.clips?.[clipId]?.type === "subtitles") {
            return;
        }

        const distanceToTime = convertUnitsToTime(distance, true);
        if (direction === ResizeDirectionEnum.LEFT) {
            // SequenceCompositionService.trimClipLeft(clipId, distanceToTime);
        } else if (direction === ResizeDirectionEnum.RIGHT) {
           // SequenceCompositionService.trimClipRight(clipId, distanceToTime);
        }
        SequenceTimelineStore.setDraggingClip(clipId, false);
        // SequenceTimelineStore.clearLeftTrimPreview(clipId);
        // SequenceTimelineStore.clearRightTrimPreview(clipId);

        clipSnapPositionsRef.current = [];
        handlePostUpdateClip();
    };

    const dragStart = (clientX: number, clientY: number) => {
        if (!elementRef.current) {
            return;
        }

        const { top, height, left, width } = elementRef.current.getBoundingClientRect();

        initialPositionRef.current = {
            top,
            height,
            left,
            width,
            x: xRef.current,
            clientXOffset: clientX - left,
            clientYOffset: clientY - top,
        };

        SequenceTimelineStore.setDraggingClip(clipId, true);
        // Don't set layer snap positions - we don't allow layer changes
        setClipSnapPositions();
    };

    const dragEnd = (_clientX: number, _clientY: number) => {
        // No layer changes allowed - just update horizontal position
        yRef.current = 0;

        handlePostUpdateClip();

        layerSnapPositionsRef.current = [];
        clipSnapPositionsRef.current = [];
    };

    // const dispatchClipDragOverDividerEvent = (index: number = -1) => {
    //     const event = new CustomEvent<ClipDragOverDividerEvent>(TimelineEventsEnum.CLIP_DRAG_OVER_DIVIDER, {
    //         detail: { clipId, index },
    //     });
    //     document.dispatchEvent(event);
    // };

    const findLayerSnapPosition = (clientY: number) => {
        let dividerIndex = -1;
        let layerSnapPositionIndex = -1;

        if (!layerSnapPositionsRef.current.length) {
            return { dividerIndex, layerSnapPositionIndex };
        }

        const firstSnapPosition = layerSnapPositionsRef.current[0];
        const lastSnapPosition = layerSnapPositionsRef.current[layerSnapPositionsRef.current.length - 1];

        if (clientY <= firstSnapPosition.top) {
            dividerIndex = 0;
        }

        if (clientY >= lastSnapPosition.top + lastSnapPosition.height) {
            dividerIndex = layerSnapPositionsRef.current.length;
        }

        if (dividerIndex > -1) {
            return { dividerIndex, layerSnapPositionIndex };
        }

        for (let i = 0; i < layerSnapPositionsRef.current.length; i++) {
            const { top: trackTop, height: trackHeight } = layerSnapPositionsRef.current[i];
            const nextPosition = layerSnapPositionsRef.current[i + 1];

            if (clientY >= trackTop && clientY <= trackTop + trackHeight) {
                layerSnapPositionIndex = i;
                break;
            }

            if (clientY >= trackTop + trackHeight && clientY <= nextPosition?.top) {
                dividerIndex = i + 1;
                break;
            }
        }

        return { dividerIndex, layerSnapPositionIndex };
    };

    const setLayerSnapPositions = () => {
        const tracks = document.querySelectorAll(`.${TRACK_CLASSNAME}`);
        layerSnapPositionsRef.current = Array.from(tracks).map((track) => {
            // @ts-ignore
            const { top, height } = track.getBoundingClientRect();
            // @ts-ignore
            return { top, height, layerId: track.id };
        });
    };

    const setClipSnapPositions = () => {
        const clips = document.querySelectorAll(`.${CLIP_CLASSNAME}`);
        const tracksContainer = document.getElementById(TIMELINE_TRACKS_CONTAINER_ID);
        const offsetLeft = tracksContainer?.offsetLeft ?? 0;

        // @ts-ignore
        // @ts-ignore

        clipSnapPositionsRef.current = Array.from(clips)
            // @ts-ignore
            .filter((clip) => clip.id !== clipId)
            .map((clip) => {
                // @ts-ignore
                const { left, width } = clip.getBoundingClientRect();
                // @ts-ignore
                return { left, width, x: left - offsetLeft, clipId: clip.id };
            });
    };

    const handlePostUpdateClip = () => {
        // New engine path: start time is independent of left trim
        const desiredStart = convertUnitsToTime(xRef.current, true);

        const actualStart = SequenceCompositionService.moveClipTo(clipId, desiredStart);

        // update local state for immediate UX
        startTimeRef.current = actualStart;
        setStartTime(actualStart);

        const uiClip = SequenceStateStore.clips?.[clipId];
        const nextStart = uiClip?.startTime ?? actualStart;
        const nextWidth = convertTimeToUnits(uiClip?.trimmedDuration ?? durationRef.current, true);

        xRef.current = convertTimeToUnits(nextStart, true);
        setX(xRef.current);
        setWidth(nextWidth);
        SequenceTimelineStore.setDraggingClip(clipId, false);
        // Use current layer from clip's track to avoid stale layerId prop
        const currentLayerId = SequenceCompositionService.getClipById(clipId)?.track?.id ?? layerId;
        updateAdjacency(currentLayerId);
    };

    const handleMouseDown = (event: React.MouseEvent) => {
        event.preventDefault();
        event.stopPropagation();

        const platform = getPlatform();
        const isAdditiveSelection = platform === "mac" ? event.metaKey : event.ctrlKey;
        focusEventRef.current = event.nativeEvent;

        if (isAdditiveSelection) {
            SequenceApplicationStore.toggleClipSelection(clipId);
            return;
        }

        const selectedIds = SequenceApplicationStore.selectedClipIds ?? [];
        const isOnlySelected = selectedIds.length === 1 && selectedIds[0] === clipId;
        if (isOnlySelected) return;
        SequenceApplicationStore.setSelectedClipId(clipId);
    };

    const handleContextMenu = (e) => {
        e.preventDefault();

        ContextMenuStore.show(e.clientX, e.clientY, [
            {
                title: "Split at playhead",
                icon: () => <ContentCutIcon/>,
                onClick: async () => {
                    await SequenceCompositionService.splitClip();
                    ContextMenuStore.hide();
                }
            },
            {
                title: "Delete",
                icon: () => <TrashIcon />,
                onClick: async () => {
                    await SequenceCompositionService.deleteClipById(clipId);
                    ContextMenuStore.hide();
                },
            },
        ]);
    };

    // Only show words strip if wordsContent is actually provided (not null/undefined)
    const hasWordsStrip = wordsContent != null;

    return (
        <div
            ref={elementRef}
            id={clipId}
            style={clipStyle}
            className={clsx(
                CLIP_CLASSNAME,
                generateClipIdentifierClass(clipId),
                {
                    [`${CLIP_CLASSNAME}__selected`]: isSelected,
                    [`${CLIP_CLASSNAME}__focused`]: isFocused,
                    [`${CLIP_CLASSNAME}__predecessor`]: hasPredecessor,
                    [`${CLIP_CLASSNAME}__successor`]: hasSuccessor,
                    [`${CLIP_CLASSNAME}--with-words`]: hasWordsStrip,
                }
            )}
            onMouseDown={handleMouseDown}
            onContextMenu={handleContextMenu}
        >
            {/* Words strip - thin section at top for transcript words */}
            {hasWordsStrip && (
                <div className={`${CLIP_CLASSNAME}__words-strip`}>
                    {wordsContent}
                </div>
            )}
            
            {/* Main content area - waveform, thumbnail, etc. */}
            <div className={`${CLIP_CLASSNAME}__content`}>
                {thumbnail != null && (
                    <div className={`${CLIP_CLASSNAME}__thumbnail-container`} style={{ opacity: 0.5 }}>
                        <div className={`${CLIP_CLASSNAME}__thumbnail`} style={{ backgroundImage: `url(${thumbnail})` }} />
                    </div>
                )}

                {children}

                <div className={`${CLIP_CLASSNAME}__content__text`}>
                    <b>{filename}</b>
                </div>
            </div>
        </div>
    );
});

export default SequenceClip;
